import authService from './authService';
import type { StockAdjustmentAuditFilters, StockAdjustmentAuditResponse } from '../types/stockAdjustmentsAudit';

/** Auditoria de ajustes de estoque — GET /reports/stock-adjustments (+ /excel). */
class StockAdjustmentsAuditService {
  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  private buildParams(filters: StockAdjustmentAuditFilters): URLSearchParams {
    const params = new URLSearchParams({ date_from: filters.date_from, date_to: filters.date_to });
    if (filters.origin_type) params.set('origin_type', String(filters.origin_type));
    if (filters.item?.trim()) params.set('item', filters.item.trim());
    if (filters.created_by?.trim()) params.set('created_by', filters.created_by.trim());
    if (filters.only_changed) params.set('only_changed', 'true');
    if (filters.sort && filters.sort !== 'date_desc') params.set('sort', filters.sort);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    return params;
  }

  async getReport(filters: StockAdjustmentAuditFilters): Promise<StockAdjustmentAuditResponse> {
    const apiClient = await authService.getApiClient();
    const response = await apiClient.get(`/reports/stock-adjustments?${this.buildParams(filters)}`);
    const body = await response.json().catch(() => null);
    if (!response.ok) throw new Error(body?.message ?? 'Erro ao carregar a auditoria de ajustes de estoque');
    return body.data;
  }

  async downloadExcel(filters: Omit<StockAdjustmentAuditFilters, 'page' | 'limit'>): Promise<void> {
    return this.download(filters, 'excel', 'auditoria-ajustes-estoque.xlsx');
  }

  /** Versão de impressão (A4 paisagem). */
  async downloadPdf(filters: Omit<StockAdjustmentAuditFilters, 'page' | 'limit'>): Promise<void> {
    return this.download(filters, 'pdf', 'auditoria-ajustes-estoque.pdf');
  }

  private async download(filters: Omit<StockAdjustmentAuditFilters, 'page' | 'limit'>, kind: 'excel' | 'pdf', fallbackName: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const params = this.buildParams(filters);

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/reports/stock-adjustments/${kind}?${params}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar ${kind === 'pdf' ? 'PDF' : 'Excel'} (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filename = disposition.match(/filename="?([^"]+)"?/)?.[1] ?? fallbackName;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
}

export const stockAdjustmentsAuditService = new StockAdjustmentsAuditService();
export default stockAdjustmentsAuditService;
